/**
 * documentSessionService — backend-persisted document session state.
 *
 * Ties the Word document's stable ``AsthrDocumentId`` custom property to a
 * ``document_sessions`` backend record that survives cross-device and
 * cross-browser reopens of the same .docx file.
 *
 * Fail-soft on all paths — a network or Office.js failure never blocks
 * the add-in. Callers fall back to localStorage (existing behaviour).
 */

import {
	type ChatPageOptions,
	type DocumentHydrateResponse,
	type DocumentSession,
	type DocumentSessionPatch,
	appendDocumentChatMessages,
	getDocumentChat,
	getDocumentHydrate,
	getDocumentSession,
	getRunChat,
	getRunSnapshot,
	getSessionToken,
	upsertDocumentSession,
} from "../../api/client";
import {
	getActiveRunId,
	getOrCreateDocumentId,
	loadDocumentId,
	loadWordHydrationMarker,
	saveDocumentId,
} from "../../services/wordWriter";
import type { ChatMessage } from "../messageProjection";

const API_BASE =
	(import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env
		?.VITE_API_BASE || "/api";

export type { DocumentSession, DocumentSessionPatch };
export type { DocumentHydrateResponse };

// ---------------------------------------------------------------------------
// Doc ID — stable for the add-in session lifetime
// ---------------------------------------------------------------------------

let _cachedDocId: string | null = null;

export function rememberRunRecoveryHint(_runId: string, _source: string): void {
	// Deprecated no-op: recovery now uses only Word custom properties.
}

/**
 * Resolve and cache the Word document's stable AsthrDocumentId.
 * Must be called after login (Office.js is available).
 * Returns ``""`` when Office.js is unavailable (browser preview / tests).
 * Never throws.
 */
export async function initDocumentSession(): Promise<string> {
	if (_cachedDocId !== null) return _cachedDocId;
	try {
		const existingDocId = await loadDocumentId();
		if (existingDocId) {
			_cachedDocId = existingDocId;
			return _cachedDocId;
		}

		const hydrationMarker = await loadWordHydrationMarker();
		const stampedRunId = hydrationMarker?.runId ?? (await getActiveRunId());
		if (stampedRunId) {
			try {
				const snapshot = await getRunSnapshot(stampedRunId);
				const recoveredDocId = snapshot.doc_id ?? null;
				if (recoveredDocId) {
					await saveDocumentId(recoveredDocId);
					_cachedDocId = recoveredDocId;
					console.info(
						"[documentSessionService] recovered doc id from run snapshot",
						{
							runId: stampedRunId,
							source: "word-props",
							docId: recoveredDocId,
						},
					);
					return _cachedDocId;
				}
			} catch (err) {
				console.warn(
					"[documentSessionService] run snapshot doc id recovery failed:",
					{
						runId: stampedRunId,
						source: "word-props",
						err,
					},
				);
			}
		}

		_cachedDocId = await getOrCreateDocumentId();
	} catch (err) {
		console.warn("[documentSessionService] getOrCreateDocumentId failed:", err);
		_cachedDocId = "";
	}
	return _cachedDocId;
}

/** Synchronous read of the cached doc_id (after initDocumentSession). Returns ``""`` if not yet resolved. */
export function getCachedDocId(): string {
	return _cachedDocId ?? "";
}

/** Replace the cached doc id. Call after ``rotateDocumentId()`` so every
 *  subsequent ``scheduleSave`` / ``getCachedDocId`` consumer routes to the
 *  new project's record. Without this, session/run/spine writes keep landing
 *  on the pre‑rotation doc_id and the post‑reload hydrate of the new id
 *  finds nothing to restore. */
export function setCachedDocId(docId: string): void {
	_cachedDocId = docId;
}

/** Clear the cached doc id so the next init re-reads Word state. */
export function clearCachedDocId(): void {
	_cachedDocId = null;
}

// ---------------------------------------------------------------------------
// Session fetch
// ---------------------------------------------------------------------------

/**
 * Fetch the persisted session for the current document.
 * Returns null on 404 (new doc) or any network failure — graceful degradation.
 */
export async function fetchDocumentSession(
	docId: string,
): Promise<DocumentSession | null> {
	if (!docId) return null;
	try {
		return await getDocumentSession(docId);
	} catch (err) {
		console.warn("[documentSessionService] fetch failed (non-fatal):", err);
		return null;
	}
}

/**
 * Fetch the full hydrate payload for the current document.
 * Returns null on network failure so callers can roll out incrementally.
 */
export async function fetchDocumentHydrate(
	docId: string,
): Promise<DocumentHydrateResponse | null> {
	if (!docId) {
		console.info("[hydrate-diag] outcome=skipped reason=no-doc-id");
		return null;
	}
	try {
		const result = await getDocumentHydrate(docId);
		if (!result) {
			console.info("[hydrate-diag] outcome=empty", { docId });
			return null;
		}
		const session = result.session;
		console.info("[hydrate-diag] outcome=hit", {
			docId,
			hasSession: Boolean(session),
			libraryId: session?.library_id ?? null,
			docCode: session?.doc_code ?? null,
			backendDocType: session?.backend_doc_type ?? null,
			conversationId: session?.conversation_id ?? null,
			runId: session?.run_id ?? null,
			spineSections: Array.isArray(session?.spine_snapshot)
				? session.spine_snapshot.length
				: 0,
			messages: Array.isArray(result.messages) ? result.messages.length : 0,
			activities: Array.isArray(result.activities)
				? result.activities.length
				: 0,
		});
		return result;
	} catch (err) {
		console.warn("[hydrate-diag] outcome=error", { docId, err });
		return null;
	}
}

export async function saveDocumentSession(
	docId: string,
	patch: DocumentSessionPatch,
): Promise<void> {
	if (!docId) return;
	await upsertDocumentSession(docId, patch);
}

// ---------------------------------------------------------------------------
// Debounced session save
// ---------------------------------------------------------------------------

let _saveTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingPatch: DocumentSessionPatch = {};
let _pendingDocId: string | null = null;
const SAVE_DEBOUNCE_MS = 300;

/**
 * Send a JSON request that survives page unload via ``keepalive: true``.
 * Browsers cap keepalive bodies at ~64 KB; document-session patches and
 * chat snapshots are well below that. Reuses the same auth/credentials
 * pattern as ``api/client.ts`` ``request()``.
 */
async function sendKeepaliveJson(
	method: string,
	path: string,
	body: unknown,
): Promise<void> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	const token = getSessionToken();
	if (token) headers.Authorization = `Bearer ${token}`;
	const resp = await fetch(`${API_BASE}${path}`, {
		method,
		credentials: "include",
		headers,
		body: JSON.stringify(body),
		keepalive: true,
	});
	if (!resp.ok) {
		throw new Error(`HTTP ${resp.status}`);
	}
}

/**
 * Schedule a debounced partial save of session state.
 * Multiple rapid calls within 300 ms are MERGED into one backend write —
 * later calls add to (not replace) the pending patch so no field is
 * silently dropped when several state updates arrive in quick succession
 * (e.g. ``activeSelection`` effect + plan save + run save within one
 * render cycle). ``clear_fields`` arrays are unioned.
 * Fire-and-forget — failures are logged, never surfaced to the writer.
 */
export function scheduleSave(docId: string, patch: DocumentSessionPatch): void {
	if (!docId) return;
	// Merge incoming patch into the accumulator. Later values win for
	// scalar fields; clear_fields arrays are unioned.
	const mergedClearFields = [
		...new Set([
			...(_pendingPatch.clear_fields ?? []),
			...(patch.clear_fields ?? []),
		]),
	];
	_pendingPatch = { ..._pendingPatch, ...patch };
	if (mergedClearFields.length > 0) {
		_pendingPatch.clear_fields = mergedClearFields;
	}
	_pendingDocId = docId;
	if (_saveTimer !== null) clearTimeout(_saveTimer);
	_saveTimer = setTimeout(() => {
		flushPendingSaves();
	}, SAVE_DEBOUNCE_MS);
}

/**
 * Cancel any pending debounce timer and POST the merged patch immediately.
 * Safe to call from anywhere (idempotent when nothing is queued).
 *
 * Returns the in-flight Promise so callers can ``await`` it before they
 * navigate away — but since this is also invoked from ``beforeunload``
 * handlers, it MUST NOT throw and MUST issue a request that survives the
 * navigation. We use ``fetch`` with ``keepalive: true`` so the browser
 * keeps the request alive after the page unloads (up to ~64 KB body).
 */
export function flushPendingSaves(): Promise<void> | void {
	if (_saveTimer !== null) {
		clearTimeout(_saveTimer);
		_saveTimer = null;
	}
	const docId = _pendingDocId;
	const patch = _pendingPatch;
	_pendingPatch = {};
	_pendingDocId = null;
	if (!docId) return;
	if (!hasPatchContent(patch)) return;
	// During an unload, axios/fetch async path may be aborted. Use the
	// keepalive flag so the browser holds the request open even after
	// the page goes away. PUT body stays JSON-shaped; backend route is
	// unchanged.
	return sendKeepaliveJson("PUT", `/document-sessions/${docId}`, patch).catch(
		(err) => {
			console.warn(
				"[documentSessionService] flush save failed (non-fatal):",
				err,
			);
		},
	);
}

function hasPatchContent(patch: DocumentSessionPatch): boolean {
	for (const key of Object.keys(patch)) {
		const value = (patch as Record<string, unknown>)[key];
		if (key === "clear_fields") {
			if (Array.isArray(value) && value.length > 0) return true;
			continue;
		}
		if (value !== undefined) return true;
	}
	return false;
}

// ---------------------------------------------------------------------------
// Chat history fetch and sync
// ---------------------------------------------------------------------------

/**
 * Page size for the chat timeline's backward pagination. The initial
 * load fetches the newest ``CHAT_PAGE_SIZE`` messages; each scroll-up
 * fetch prepends one more page.
 */
export const CHAT_PAGE_SIZE = 50;

/** One page of typed chat history plus the backward-pagination flag. */
export interface ChatHistoryPage {
	messages: ChatMessage[];
	/** True when older messages exist on the backend before this page. */
	hasMore: boolean;
}

/**
 * Fetch persisted chat messages for a document.
 * Returns null on 404 (no history yet) or network failure.
 */
export async function fetchDocumentChat(
	docId: string,
	opts?: ChatPageOptions,
): Promise<ChatHistoryPage | null> {
	if (!docId) return null;
	try {
		const page = await getDocumentChat(docId, opts);
		if (page === null) return null;
		return { messages: page.messages as unknown as ChatMessage[], hasMore: page.hasMore };
	} catch (err) {
		console.warn(
			"[documentSessionService] chat fetch failed (non-fatal):",
			err,
		);
		return null;
	}
}

/**
 * Fetch chat messages scoped to a specific run. Server returns the
 * union of messages tagged with this run_id and session-level messages
 * (run_id null), so loading an older run hydrates the chat to that
 * run's history without dropping the doc-wide greeting / selection
 * context.
 *
 * Returns null on failure so callers can fall back gracefully.
 */
export async function fetchRunChat(
	runId: string,
	opts?: ChatPageOptions,
): Promise<ChatHistoryPage | null> {
	if (!runId) return null;
	try {
		const page = await getRunChat(runId, opts);
		if (page === null) return null;
		return { messages: page.messages as unknown as ChatMessage[], hasMore: page.hasMore };
	} catch (err) {
		console.warn(
			"[documentSessionService] run chat fetch failed (non-fatal):",
			err,
		);
		return null;
	}
}

let _chatSyncTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingChatQueue: ChatMessage[] = [];
let _pendingChatDocId: string | null = null;
let _pendingChatRunId: string | null = null;
const CHAT_SYNC_DEBOUNCE_MS = 500;

function _queueKey(m: ChatMessage): string {
	return (m as { _id?: string })._id ?? `${m.at}:${m.role}`;
}

/**
 * Schedule a debounced append of new chat messages to the backend.
 * Accepts only the delta (newly projected messages) — not the full array.
 * Multiple rapid calls within 500 ms coalesce into one POST.
 * Fire-and-forget — failures are logged, never surfaced.
 */
export function scheduleChatSync(
	docId: string,
	newMessages: ChatMessage[],
	runId: string | null = null,
): void {
	if (!docId) return;
	_pendingChatDocId = docId;
	_pendingChatRunId = runId;
	// Merge into the queue, deduping by _id (or at:role for messages without one).
	const seen = new Set(_pendingChatQueue.map(_queueKey));
	for (const m of newMessages) {
		if (!seen.has(_queueKey(m))) {
			_pendingChatQueue.push(m);
			seen.add(_queueKey(m));
		}
	}
	if (_chatSyncTimer !== null) clearTimeout(_chatSyncTimer);
	_chatSyncTimer = setTimeout(() => {
		flushPendingChatSync();
	}, CHAT_SYNC_DEBOUNCE_MS);
}

/**
 * Cancel any pending chat-sync debounce and POST the queued messages
 * immediately. Uses ``keepalive: true`` so the request survives page
 * unload. Idempotent when nothing is queued.
 *
 * On success the queue is cleared. On failure the queue is kept so the
 * next flush retries the same messages.
 */
export function flushPendingChatSync(): Promise<void> | void {
	if (_chatSyncTimer !== null) {
		clearTimeout(_chatSyncTimer);
		_chatSyncTimer = null;
	}
	const docId = _pendingChatDocId;
	const queue = _pendingChatQueue;
	const runId = _pendingChatRunId;
	if (!docId || queue.length === 0) return;

	// Drain the queue optimistically; restore on failure.
	_pendingChatQueue = [];

	return appendDocumentChatMessages(docId, queue as unknown as Record<string, unknown>[], runId).catch((err) => {
		console.warn(
			"[documentSessionService] flush chat sync failed (non-fatal):",
			err,
		);
		// Restore unsynced messages to the front of the queue so the next
		// flush retries them.
		_pendingChatQueue = [...queue, ..._pendingChatQueue];
	});
}

/**
 * Flush every pending debounced backend write — session patch + chat sync.
 * Designed for ``beforeunload`` / ``visibilitychange='hidden'`` /
 * ``Office.onClose`` handlers so no recently-edited UI state is lost when
 * the writer closes the pane within the debounce window.
 */
export function flushAllPendingSaves(): void {
	flushPendingSaves();
	flushPendingChatSync();
}
